import gettext from "@/language";
import app from "@/main";
const { $gettext, $pgettext } = gettext;

const arrayToMap = (arr) => {
  return arr.reduce((map, obj) => {
    map[obj.id] = obj;
    return map;
  }, {});
};

const CommonService = {
  languages: [
    { id: "sl", value: "Slovenščina", icon: "flag-sl" },
    { id: "en", value: "English", icon: "flag-en" },
  ],

  yesNo: [
    { id: "true", name: $gettext("Yes") },
    { id: "false", name: $gettext("No") },
  ],

  guestStatus: [
    { id: "ACTIVE", name: $gettext("Active"), class: "badge-success" },
    { id: "DEACTIVATED", name: $gettext("Deactivated"), class: "badge-danger" },
    { id: "PENDING", name: $gettext("Pending"), class: "badge-info" },
  ],

  statuses: [
    { id: "ERROR", name: $gettext("Error"), class: "badge-danger" },
    { id: "CHECKIN", name: $gettext("Checkin"), class: "badge-success" },
    { id: "CHECKOUT", name: $gettext("Checkout"), class: "badge-warning" },
  ],

  modules: [
    { id: "PLUS_ONES", name: $gettext("Plus ones"), class: "badge-color-1" },
    { id: "PARTNERS", name: $gettext("Partners"), class: "badge-color-2" },
    { id: "TICKETING_INTEGRATION", name: $gettext("Ticketing integration"), class: "badge-color-3" },
    { id: "SEND_TICKETS_TO_GUESTS", name: $gettext("Send ticket to guests"), class: "badge-color-4" },
  ],

  photoCompressionTypes: {
    COVER: "COVER",
    LOGO: "LOGO",
    THUMBNAIL: "THUMBNAIL",
    UNCOMPRESSED: "UNCOMPRESSED",
  },

  routes: {
    ORGANIZATIONS: {
      title: $gettext("Organizations"),
      icon: "icon-business-unit",
      path: "app.organizations.all",
    },
    EVENTS: {
      title: $gettext("Events"),
      icon: "icon-copy",
      path: "app.organizations.events.all",
    },
    USERS: {
      title: $gettext("Users"),
      icon: "icon-user-check",
      path: "app.organizations.users",
    },
    PARTNERS: {
      title: $gettext("Partners"),
      icon: "icon-share-2",
      path: "app.organizations.partners",
    },
    GUESTS: {
      title: $gettext("Guests"),
      icon: "icon-users",
      path: "app.organizations.events.guests",
    },
    GROUPS: {
      title: $gettext("Groups"),
      icon: "icon-grid",
      path: "app.organizations.events.groups",
    },
    DASHBOARD: {
      title: $gettext("Dashboard"),
      icon: "icon-home",
      path: "app.organizations.events.dashboard",
    },
    CHECKIN_LOGS: {
      title: $gettext("Checkin logs"),
      icon: "icon-clipboard",
      path: "app.organizations.events.checkin-logs",
    },
    CHECKIN: {
      title: $gettext("Checkin"),
      icon: "icon-check-square",
      path: "app.organizations.events.checkin",
    },
    SANDBOX: {
      title: $gettext("Sandbox"),
      icon: "icon-life-buoy",
      path: "app.sandbox",
    },
  },

  permissions: [
    {
      parent: true,
      id: "partners",
      name: $pgettext("Permissions", "Partners"),
      children: [
        { id: "can_view_partners", text: $pgettext("Permissions", "View partners") },
        { id: "can_create_partners", text: $pgettext("Permissions", "Create partners") },
        { id: "can_edit_partners", text: $pgettext("Permissions", "Edit partners") },
      ],
    },
    {
      parent: true,
      id: "users",
      name: $pgettext("Permissions", "Users"),
      children: [
        { id: "can_view_users", text: $pgettext("Permissions", "View users") },
        { id: "can_manage_users", text: $pgettext("Permissions", "Manage users") },
      ],
    },
    {
      parent: true,
      id: "events",
      name: $pgettext("Permissions", "Events"),
      children: [
        { id: "can_create_events", text: $pgettext("Permissions", "Create events") },
        { id: "can_edit_events", text: $pgettext("Permissions", "Edit events") },
      ],
    },
    {
      parent: true,
      id: "guests",
      name: $pgettext("Permissions", "Guests"),
      children: [
        { id: "can_create_guests", text: $pgettext("Permissions", "Create guests") },
        { id: "can_edit_guests", text: $pgettext("Permissions", "Edit guests") },
        { id: "can_deactivate_guests", text: $pgettext("Permissions", "Deactivate guests") },
        { id: "can_reactivate_guests", text: $pgettext("Permissions", "Reactivate guests") },
        { id: "can_checkin_guests", text: $pgettext("Permissions", "Checkin guests") },
        { id: "can_confirm_guests", text: $pgettext("Permissions", "Confirm guests") },
      ],
    },
    {
      parent: true,
      id: "guest_groups",
      name: $pgettext("Permissions", "Guest groups"),
      children: [
        { id: "can_create_guest_groups", text: $pgettext("Permissions", "Create groups") },
        { id: "can_edit_guest_groups", text: $pgettext("Permissions", "Edit group") },
      ],
    },
  ],

  getPermission: (key) => {
    return CommonService.permissionsMap[key] || { text: key };
  },

  getModule: (key) => {
    return CommonService.modulesMap[key] || { name: key };
  },

  getGuestStatus: (key) => {
    return CommonService.guestStatusMap[key] || { name: key };
  },

  searchYesNo: (search) => {
    if (!search) return CommonService.yesNo;
    return CommonService.yesNo.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
  },

  success: (text, title = $gettext("Success"), icon = "icon-check-circle") => {
    app.config.globalProperties.$nice.notification(text, "success", title, icon);
  },

  error: (text, title = $gettext("Error"), icon = "icon-alert-triangle") => {
    app.config.globalProperties.$nice.notification(text, "danger", title, icon);
  },

  isLocal: document.location.hostname == "localhost" || document.location.hostname == "0.0.0.0" || document.location.hostname == "test.olaii.com",
  isProduction: process.env.NODE_ENV == "production",

  generateInviteLink: function (uuid) {
    let baseURL = "web.olaii.com";
    if (CommonService.isLocal) baseURL = "test.olaii.com:9007";
    else if (!CommonService.isProduction) baseURL = "web.dev.olaii.com";
    return `https://${baseURL}/guestlist/invite/${uuid}/`;
  },

  /**
   * Copies a string of text to the clipboard.
   * @param {string} text The text to copy.
   * @returns {Promise<boolean>} A promise that resolves to true if successful, false otherwise.
   */
  async copyTextToClipboard(text) {
    // Check if the modern Clipboard API is supported by the browser
    if (!navigator.clipboard) {
      CommonService.error($gettext("Copy to clipboard failed"));
      return false;
    }

    try {
      await navigator.clipboard.writeText(text);
      const successMessage = `'${text}' ${$gettext("copied to clipboard")}`;
      // eslint-disable-next-line no-console
      console.log(successMessage);
      CommonService.success(successMessage);
      return true;
    } catch (err) {
      CommonService.error($gettext("Copy to clipboard failed"));
      return false;
    }
  },

  /**
   * Downloads a Blob object as a file with a specified filename.
   * @param {Blob} blob - The Blob object to download.
   * @param {string} fileName - The desired name for the downloaded file (e.g., 'report.pdf').
   */
  downloadBlob(blob, fileName) {
    // 1. Create a URL for the Blob
    const blobUrl = URL.createObjectURL(blob);

    // 2. Create a temporary anchor element
    const link = document.createElement("a");

    // 3. Set attributes
    link.href = blobUrl;
    link.download = fileName;

    // Append to the document body (necessary for Firefox)
    document.body.appendChild(link);

    // 4. Simulate the click
    link.click();

    // 5. Clean up by revoking the Blob URL
    // We use setTimeout to ensure the browser has started the download before revoking the URL
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      // Remove the element from the DOM
      document.body.removeChild(link);
    }, 100);
  },

  generateRandomColor: () => {
    // Base value for a light color (e.g., 190, or #BE in hex)
    const base = 190;
    // Range to add randomly (e.g., up to 65, or 0x41)
    const range = 65;

    // Generate three high, yet slightly varying, RGB components
    let R = (base + Math.floor(Math.random() * range)).toString(16).padStart(2, "0");
    let G = (base + Math.floor(Math.random() * range)).toString(16).padStart(2, "0");
    let B = (base + Math.floor(Math.random() * range)).toString(16).padStart(2, "0");

    // To ensure the color is not pure white (#FFFFFF), we'll swap the
    // most significant component with a random lower value.
    const components = [R, G, B];

    // Randomly select one component to be slightly darker (lower value)
    const randomIndex = Math.floor(Math.random() * 3);

    // Generate a random low value (e.g., 90 to 140, or #5A to #8C)
    const lowValue = (90 + Math.floor(Math.random() * 50)).toString(16).padStart(2, "0");

    components[randomIndex] = lowValue;

    return `#${components.join("")}`;
  },

  downloadFile: (responseData, filename) => {
    const blob = new Blob([responseData]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  },
};

CommonService.permissionsMap = (function () {
  let allPermissions = [];
  CommonService.permissions.forEach((permissionGroup) => {
    allPermissions = [...allPermissions, ...permissionGroup.children];
  });
  return arrayToMap(allPermissions);
})();
CommonService.guestStatusMap = arrayToMap(CommonService.guestStatus);
CommonService.statusesMap = arrayToMap(CommonService.statuses);
CommonService.modulesMap = arrayToMap(CommonService.modules);

export default CommonService;
